import { useEffect, useState, useCallback } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi, fetchApiRef, configApiRef } from '@backstage/core-plugin-api';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { emitProjectSelected } from './harnessProjectBus';

interface HarnessProject {
  identifier: string;
  name: string;
  orgIdentifier: string;
}

export const HarnessProjectPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
}: FieldExtensionComponentProps<string>) => {
  const fetchApi = useApi(fetchApiRef);
  const configApi = useApi(configApiRef);
  const [projects, setProjects] = useState<HarnessProject[]>([]);
  const [selected, setSelected] = useState<string>(formData ?? '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  const backendBaseUrl = configApi.getString('backend.baseUrl');

  useEffect(() => {
    const fetchAllProjects = async () => {
      let page = 0;
      let allItems: HarnessProject[] = [];
      let hasMore = true;

      while (hasMore) {
        const res = await fetchApi.fetch(
          `${backendBaseUrl}/api/proxy/harness-projects?accountIdentifier=nYY7inrwTrqqa3r1a_-krg&pageSize=500&pageIndex=${page}`,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const items: HarnessProject[] = (data?.data?.content ?? []).map(
          (item: { project: HarnessProject }) => item.project,
        );
        allItems = allItems.concat(items);
        const totalPages = data?.data?.totalPages ?? 1;
        page += 1;
        hasMore = page < totalPages;
      }

      return allItems;
    };

    fetchAllProjects()
      .then(items => {
        setProjects(items.sort((a, b) => a.name.localeCompare(b.name)));
        setLoading(false);
      })
      .catch(err => {
        setError(String(err.message ?? err));
        setLoading(false);
      });
  }, [fetchApi, backendBaseUrl]);

  // Emit org AFTER projectId onChange has been committed to RJSF.
  // Calling both onChange and emitProjectSelected synchronously causes RJSF
  // to receive two rapid onChange calls where the second (orgId) can overwrite
  // the first (projectId) with stale state, making the Review page show the old project.
  useEffect(() => {
    if (!selected || projects.length === 0) return;
    const project = projects.find(p => p.identifier === selected);
    if (project) emitProjectSelected(project.orgIdentifier);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const handleChange = useCallback(
    (e: SelectChangeEvent<string>) => {
      const projectId = e.target.value;
      setSelected(projectId);
      onChange(projectId);
    },
    [onChange],
  );

  return (
    <FormControl
      fullWidth
      error={!!rawErrors?.length || !!error}
      required={required}
    >
      <InputLabel shrink>{schema.title ?? 'Project'}</InputLabel>
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Select value={selected} onChange={handleChange} displayEmpty>
          <MenuItem value="" disabled>
            Select a project
          </MenuItem>
          {projects.map(p => (
            <MenuItem key={p.identifier} value={p.identifier}>
              {p.name}
            </MenuItem>
          ))}
        </Select>
      )}
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};
